import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';

import useImageData, { UseImageDataAttr } from '@hooks/imageHooks/useImageData';
import useSnack from '@hooks/useSnack';
import { ImageType, ImageTypes } from '@models/Image';

import ImageGroupDataContext, { ImageGroupDataValues } from './context';

export type ImageGroupDataProviderProps = PropsWithChildren<
  Pick<ImageGroupDataValues, 'defaultImageType' | 'defaultImageAspectRatio'> &
    Pick<ImageGroupDataValues, 'handleUploadImage'> &
    Partial<Pick<ImageGroupDataValues, 'disabledImageTypes'>> & {
      filenameBase: string;
      /**
       * Function to get the default image URL for a given image. Default images cached, and are used when there are no uploaded images for the current context.
       */
      getDefaultImageUrl?: (imageType: ImageType) => string;
      /**
       * Function to get the image URL for a given image ID and type. These are always fetched fresh (no cache).
       */
      getImageUrl?: (imageId: string, imageType: ImageType) => string;
      handleDeleteImage?: (imageType: ImageType) => Promise<ApiResponse<unknown>>;
      /**
       * Used to trigger image refetch from outside the component
       */
      hash?: number;
      imageId?: string;
      /**
       * Unique identifier used to ensure
       */
      queryKey?: string;
    }
>;

const ImageGroupDataProvider: FC<ImageGroupDataProviderProps> = ({
  children,
  defaultImageType,
  disabledImageTypes = [],
  filenameBase,
  getDefaultImageUrl,
  getImageUrl,
  defaultImageAspectRatio,
  handleDeleteImage,
  handleUploadImage,
  imageId,
}) => {
  const { addSnack } = useSnack();
  const { t } = useTranslation();

  const id = useId();

  const getImageDataAttr = (imageType: ImageType): UseImageDataAttr => ({
    defaultSrc: getDefaultImageUrl?.(imageType),
    disabled: disabledImageTypes.includes(imageType),
    expectsDeleteHandler: Boolean(handleDeleteImage),
    handleDeleteImage: handleDeleteImage ? () => handleDeleteImage(imageType) : undefined,
    identifier: imageId ?? id,
    imageType,
    src: imageId ? getImageUrl?.(imageId, imageType) : undefined,
  });

  const wide = useImageData(getImageDataAttr('wide'));
  const square = useImageData(getImageDataAttr('square'));
  const poster = useImageData(getImageDataAttr('poster'));

  const hookResults = useMemo(() => ({ wide, square, poster }), [wide, square, poster]);

  const contextImages = useMemo(
    () => ({
      wide: wide.contextImageMetadata,
      square: square.contextImageMetadata,
      poster: poster.contextImageMetadata,
    }),
    [wide, square, poster]
  );

  const defaultImages = useMemo(
    () => ({
      wide: wide.defaultImageMetadata,
      square: square.defaultImageMetadata,
      poster: poster.defaultImageMetadata,
    }),
    [wide, square, poster]
  );

  const fallbackImages = useMemo(
    () => ({
      wide: wide.fallbackImageMetadata,
      square: square.fallbackImageMetadata,
      poster: poster.fallbackImageMetadata,
    }),
    [wide, square, poster]
  );

  const imageMap = useMemo(
    () => ({
      wide: wide.effectiveImageMetadata,
      square: square.effectiveImageMetadata,
      poster: poster.effectiveImageMetadata,
    }),
    [wide, square, poster]
  );

  const [deleting, setDeleting] = useState<ImageType[]>([]);
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const refreshImages = useCallback(
    (imageType?: ImageType | ImageType[]) => {
      const imageTypesToRefresh = imageType
        ? Array.isArray(imageType)
          ? imageType
          : [imageType]
        : [...ImageTypes];

      Object.entries(hookResults).forEach(
        ([imageType, { refetch }]) =>
          imageTypesToRefresh.includes(imageType as ImageType) && refetch?.()
      );
    },
    [hookResults]
  );

  const deleteImageFn = useCallback(
    async (imageType: ImageType) => {
      if (!handleDeleteImage) return;
      setDeleting(prev => [...prev, imageType]);
      try {
        const { ok, originalError } = await handleDeleteImage(imageType);
        if (originalError) throw originalError;
        if (!ok) throw new Error();
        addSnack(t('deleteItem.success', { contentType: t('contentType.image') }));
        refreshImages(imageType);
      } catch {
        addSnack(
          t('deleteItem.error', { contentType: t('contentType.image').toLocaleLowerCase() }),
          { variant: 'error' }
        );
      } finally {
        setDeleting(prev => prev.filter(it => it !== imageType));
      }
    },
    [handleDeleteImage, addSnack, t, refreshImages]
  );

  const { mutate: deleteImage } = useMutation({
    mutationFn: async (imageType: ImageType) => {
      if (timer.current) clearTimeout(timer.current);
      await new Promise<void>(resolve => {
        timer.current = setTimeout(async () => {
          await deleteImageFn(imageType);
          resolve();
        }, 200);
      });
    },
  });

  const hasContextImages = useMemo(
    () => Object.values(contextImages).some(img => !!img?.wasFound),
    [contextImages]
  );

  const hasDefaultImages = useMemo(
    () => Object.values(defaultImages).some(img => !!img?.wasFound),
    [defaultImages]
  );

  const isDeleting = useCallback(
    (imageType: ImageType) => deleting.includes(imageType),
    [deleting]
  );

  const loading = Object.values(hookResults).some(({ isLoading }) => isLoading);

  const defaultImageMissing = Boolean(
    !loading &&
      defaultImageType &&
      Object.values(contextImages).some(Boolean) &&
      !contextImages[defaultImageType]?.wasFound
  );

  const values = useMemo<ImageGroupDataValues>(
    () => ({
      contextImages,
      defaultImageMissing,
      defaultImages,
      defaultImageType,
      deleteImage,
      disableDelete: !handleDeleteImage,
      disabledImageTypes,
      fallbackImages,
      filenameBase,
      handleUploadImage,
      defaultImageAspectRatio,
      hasContextImages,
      hasDefaultImages,
      imageMap,
      isDeleting,
      loading,
      refreshImages,
    }),
    [
      contextImages,
      defaultImageMissing,
      defaultImages,
      defaultImageType,
      deleteImage,
      disabledImageTypes,
      defaultImageAspectRatio,
      fallbackImages,
      filenameBase,
      handleDeleteImage,
      handleUploadImage,
      hasContextImages,
      hasDefaultImages,
      imageMap,
      isDeleting,
      loading,
      refreshImages,
    ]
  );

  return <ImageGroupDataContext.Provider value={values}>{children}</ImageGroupDataContext.Provider>;
};

export default ImageGroupDataProvider;
